import { sanitizeBusinessMessage, toUserFacingApiError } from '../utils/userFacingError'
import { api } from './api'

function assertSuccess(body: unknown) {
  const b = body as { success?: boolean; message?: string; data?: unknown }
  if (!b?.success) {
    throw new Error(sanitizeBusinessMessage(b?.message, 'Request failed'))
  }
  return b.data
}

function apiError(err: unknown, fallback: string) {
  return toUserFacingApiError(err, fallback)
}

function storeMembersPath(storeId: number, suffix = '') {
  return `/storebusiness/stores/${encodeURIComponent(String(storeId))}/members${suffix}`
}

export type TeamMemberRecord = {
  id: number
  accountId?: number
  email: string
  firstName: string
  lastName: string
  role: 'store_owner' | 'staff' | 'org_admin' | string
  mustChangePassword?: boolean
  modules?: string[] | null
  status?: string
  createdAt?: string
}

export async function listTeamMembers(storeId: number): Promise<{ items: TeamMemberRecord[] }> {
  try {
    const { data } = await api.get(storeMembersPath(storeId))
    const payload = assertSuccess(data) as { items?: TeamMemberRecord[] }
    return { items: Array.isArray(payload.items) ? payload.items : [] }
  } catch (e) {
    throw apiError(e, 'Failed to load team members')
  }
}

export async function createStaffMember(
  storeId: number,
  input: {
    firstName: string
    lastName: string
    email: string
    modules: string[]
  },
): Promise<{ user: TeamMemberRecord; emailSent: boolean }> {
  try {
    const { data } = await api.post(storeMembersPath(storeId), input)
    return assertSuccess(data) as { user: TeamMemberRecord; emailSent: boolean }
  } catch (e) {
    throw apiError(e, 'Failed to add staff member')
  }
}

export async function resetStaffCredentials(
  storeId: number,
  membershipId: number,
): Promise<{ user: TeamMemberRecord; emailSent: boolean }> {
  try {
    const { data } = await api.post(
      storeMembersPath(storeId, `/${encodeURIComponent(String(membershipId))}/reset-credentials`),
    )
    return assertSuccess(data) as { user: TeamMemberRecord; emailSent: boolean }
  } catch (e) {
    throw apiError(e, 'Failed to reset staff login')
  }
}

export async function deactivateStaffMember(
  storeId: number,
  membershipId: number,
): Promise<{ deactivated: boolean; membershipId: number }> {
  try {
    const { data } = await api.delete(storeMembersPath(storeId, `/${encodeURIComponent(String(membershipId))}`))
    return assertSuccess(data) as { deactivated: boolean; membershipId: number }
  } catch (e) {
    throw apiError(e, 'Failed to deactivate staff member')
  }
}

export async function updateStaffModules(
  storeId: number,
  membershipId: number,
  modules: string[],
): Promise<{ user: TeamMemberRecord }> {
  try {
    const { data } = await api.patch(storeMembersPath(storeId, `/${encodeURIComponent(String(membershipId))}/modules`), {
      modules,
    })
    return assertSuccess(data) as { user: TeamMemberRecord }
  } catch (e) {
    throw apiError(e, 'Failed to update module access')
  }
}
